import React, { useState } from 'react';
import { Lock } from 'lucide-react';

interface ErrorObject {
  type?: string;
  message?: string;
  details?: any;
}

interface OTPVerificationFormProps {
  onSubmit: (otpCode: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  phoneNumber: string;
  loading?: boolean;
  error?: string | ErrorObject | null;
  maxLength?: number;
  accentColor?: 'blue' | 'green' | 'purple';
}

const OTPVerificationForm: React.FC<OTPVerificationFormProps> = ({
  onSubmit,
  onResend,
  onBack,
  phoneNumber,
  loading = false,
  error,
  maxLength = 6,
  accentColor = 'green',
}) => {
  const [otpCode, setOtpCode] = useState('');

  const handleOTPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, maxLength);
    setOtpCode(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length === maxLength) {
      await onSubmit(otpCode);
    }
  };

  const accentButtonClasses = {
    blue: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white',
    green: 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] text-black',
    purple: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white',
  };

  const accentBorderClasses = {
    blue: 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/50',
    green: 'border-[#33FF00]/30 text-[#33FF00] hover:bg-[#33FF00]/10 hover:border-[#33FF00]/50',
    purple: 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50',
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {/* OTP Input */}
      <div>
        <label htmlFor="otp_code" className="block text-sm font-medium text-gray-300 mb-2">
          Verification Code
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            id="otp_code"
            type="tel"
            value={otpCode}
            onChange={handleOTPChange}
            className="w-full px-4 py-3 pl-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-[#33FF00]/50 focus:ring-2 focus:ring-[#33FF00]/20 transition-all text-center text-2xl tracking-widest"
            placeholder="000000"
            maxLength={maxLength}
            autoComplete="one-time-code"
            autoFocus
          />
        </div>
        <p className="mt-1 text-xs text-gray-400">Enter the {maxLength}-digit code sent to {phoneNumber}</p>
        {otpCode && otpCode.length !== maxLength && (
          <p className="mt-1 text-sm text-red-400">Please enter a valid {maxLength}-digit code</p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4">
          <p className="text-sm text-red-400">
            {typeof error === 'string' 
              ? error 
              : error?.message || error?.details || 'An error occurred'}
          </p>
        </div>
      )}

      {/* Verify Button */}
      <button
        type="submit"
        disabled={loading || otpCode.length !== maxLength}
        className={`w-full py-3 ${accentButtonClasses[accentColor]} disabled:from-gray-600 disabled:to-gray-700 text-black disabled:text-white rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:transform-none`}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
            Verifying...
          </div>
        ) : (
          'Verify Code'
        )}
      </button>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          className={`flex-1 py-3 bg-white/5 backdrop-blur-sm border ${accentBorderClasses[accentColor]} rounded-2xl font-medium transition-all duration-300`}
          onClick={onResend}
          disabled={loading}
        >
          Resend Code
        </button>
        <button
          type="button"
          className="flex-1 py-3 bg-white/5 backdrop-blur-sm border border-white/20 text-gray-300 hover:bg-white/10 hover:border-white/30 rounded-2xl font-medium transition-all duration-300"
          onClick={onBack}
          disabled={loading}
        >
          Different Number
        </button>
      </div>
    </form>
  );
};

export default OTPVerificationForm;

